/**
 * AI Clinical Analysis Validator Service
 * Validates messages in Clinical Analysis tab BEFORE sending to backend
 * Common validation logic for ALL pages (Hospital, Community Transfer, Deceased)
 */

class AIClinicalAnalysisValidatorService {
    /**
     * Validate if a message should be blocked in Clinical Analysis tab
     * @param {string} message - User message
     * @param {boolean} isClinicalAnalysisTab - True if currently in Clinical Analysis tab
     * @returns {Object} Validation result with shouldBlock, message, and actionType
     */
    validate(message, isClinicalAnalysisTab) {
        // If not in Clinical Analysis tab, allow everything
        if (!isClinicalAnalysisTab) {
            return {
                shouldBlock: false,
                isValid: true,
                message: null,
                actionType: null
            };
        }

        const lowerMessage = message.toLowerCase().trim();

        // UI Actions that require Trend Analysis tab - COMPREHENSIVE list (matches backend)
        const uiActionVerbs = [
            // Chart/Dialog opening
            'open chart', 'open dialog', 'open list', 'show chart', 'display chart',
            'view chart', 'see chart', 'pull up chart', 'bring up chart',
            'open insurance', 'open doctor', 'open floor', 'open diagnosis', 'open hospital',
            'show insurance', 'show doctor', 'show floor', 'show diagnosis', 'show hospital',
            'open per', 'show per', // Generic "open per X" or "show per X"
            
            // Report generation
            'create report', 'generate report', 'build report', 'make report',
            'export', 'download', 'pdf', 'excel', 'csv',
            'export to', 'download to', 'save as',
            
            // Alert setup
            'setup alert', 'create alert', 'set alert', 'configure alert',
            'setup automated', 'create automated', 'schedule report',
            
            // Date changes
            'change date', 'switch date', 'set date', 'update date',
            'set date range', 'change date range', 'apply date',
            
            // Filter actions
            'apply filter', 'add filter', 'set filter', 'filter by',
            'show only', 'filter to', 'filter for',
            
            // Census/Percentage changes
            'change census', 'switch census', 'toggle census',
            'change percentage', 'switch percentage'
        ];

        // Percentage/Census method changes that require Trend Analysis tab
        const percentageChangeActions = [
            'percentage by bed', 'percentage by capacity', 'bed capacity percentage',
            'change percentage', 'switch percentage', 'set percentage', 
            'percentage method', 'percentage to bed', 'percentage to census',
            'change to bed capacity', 'change to census', 'change to average',
            'use bed capacity', 'use census', 'use average census',
            'calculate by bed', 'calculate by census', 'calculate by capacity',
            'show bed capacity percentage', 'show census percentage',
            'switch to bed', 'switch to census', 'switch to average',
            'percentage base', 'percentage basis', 'change percentage base'
        ];

        // Check if message contains UI action verbs
        const isExplicitUIAction = uiActionVerbs.some(verb => lowerMessage.includes(verb));
        const isPercentageChangeAction = percentageChangeActions.some(action => lowerMessage.includes(action));

        // Debug logging to see what's being matched
        // console.log('🔍 [Clinical Analysis Validator] Checking message:', {
        //     message: message.substring(0, 100),
        //     lowerMessage: lowerMessage.substring(0, 100),
        //     isExplicitUIAction,
        //     isPercentageChangeAction,
        //     matchedUIVerbs: uiActionVerbs.filter(verb => lowerMessage.includes(verb)),
        //     matchedPercentageActions: percentageChangeActions.filter(action => lowerMessage.includes(action))
        // });

        if (isExplicitUIAction || isPercentageChangeAction) {
            const actionType = isPercentageChangeAction ? 'percentage_change' : 'ui_action';
            const validationMessage = this.getValidationMessage(actionType);

            // console.log('🚫 [Clinical Analysis Validator] UI action detected - BLOCKING API call:', {
            //     message: message.substring(0, 100),
            //     actionType,
            //     matchedKeywords: isExplicitUIAction 
            //         ? uiActionVerbs.filter(verb => lowerMessage.includes(verb))
            //         : percentageChangeActions.filter(action => lowerMessage.includes(action))
            // });

            return {
                shouldBlock: true,
                isValid: false,
                message: validationMessage,
                actionType: actionType,
                suggestedAction: 'switch_to_trend_analysis'
            };
        }

        // Message is valid for Clinical Analysis
        // console.log('✅ [Clinical Analysis Validator] Message is valid for Clinical Analysis tab');
        return {
            shouldBlock: false,
            isValid: true,
            message: null,
            actionType: null
        };
    }

    /**
     * Get validation message based on action type
     * @param {string} actionType - Type of blocked action (ui_action or percentage_change)
     * @returns {string} Validation message to display
     */
    getValidationMessage(actionType) {
        if (actionType === 'percentage_change') {
            return `📊 **Wrong Tab** - Please use the **Trend Analysis tab** to change percentage methods.

The **Clinical Analysis tab** is designed for comprehensive patient data analysis and insights. To change percentage calculations (like switching to "Bed Capacity", "Census as of Now", or "Average Census"), please switch to the **Trend Analysis tab** where you have access to the percentage method controls.

💡 **Tip:** This tab is for asking analytical questions about patient data. You can ask questions like "What are the trends in patient admissions?" or "Which patients are at high risk?" here!`;
        } else {
            return `📝 **Wrong Tab** - Please use the **Trend Analysis tab** for UI actions.

The **Clinical Analysis tab** is designed for comprehensive patient data analysis. To perform UI actions like:
- Opening charts
- Creating reports
- Setting up alerts
- Changing dates
- Applying filters
- Generating exports

Please switch to the **Trend Analysis tab**.

💡 **Tip:** This tab is for asking questions and getting insights about patient data - feel free to ask analytical questions here!`;
        }
    }

    /**
     * Create a validation message object for display in chat
     * @param {string} validationMessage - The validation message text
     * @param {string} actionType - Type of blocked action
     * @returns {Object} Message object for chat display
     */
    createValidationMessageObject(validationMessage, actionType) {
        return {
            id: Date.now(),
            type: 'ai',
            content: validationMessage,
            timestamp: new Date(),
            isValidationMessage: true,
            blockedActionType: actionType,
            suggestions: [],
            insights: [],
            actions: [{
                type: 'switch_to_regular_chat',
                action: 'switch_tab',
                description: 'Switch to Trend Analysis tab for dashboard actions',
                buttonText: 'Switch to Trend Analysis',
                actionId: `switch_tab_${Date.now()}`,
                isNotesAnalysisAction: true
            }],
            hasActions: true
        };
    }
}

// Create singleton instance
const aiClinicalAnalysisValidatorService = new AIClinicalAnalysisValidatorService();

export default aiClinicalAnalysisValidatorService;

